"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import { css, sva } from "@/../styled-system/css";
import Cross from "@/assets/svg/x.svg";

const dialog = sva({
  slots: [
    "overlay",
    "close",
    "content",
    "header",
    "footer",
    "title",
    "description",
  ],
  base: {
    overlay: {
      position: "fixed",
      inset: "0",
      zIndex: "50",
      bgColor: "black/50",
      "&[data-state=open]": {
        animateIn: true,
        fadeIn: 0,
      },
      "&[data-state=closed]": {
        animateOut: true,
        fadeOut: 0,
      },
    },
    close: {
      rounded: "sm",
      opacity: "0.7",
      transition: "opacity",
      cursor: "pointer",
      _hover: { opacity: "1" },
      _disabled: { pointerEvents: "none" },
    },
    content: {
      position: "fixed",
      left: "50%",
      top: "50%",
      zIndex: "50",
      display: "flex",
      flexDirection: "column",
      w: "full",
      maxW: "lg",
      translate: "auto",
      translateX: "-50%",
      translateY: "-50%",
      transitionDuration: "normal",
      gap: "5",
      p: "8",
      shadow: "lg",
      bgColor: "white",
      "&[data-state=open]": {
        animateIn: true,
        fadeIn: 0,
        zoomIn: 95,
      },
      "&[data-state=closed]": {
        animateOut: true,
        fadeOut: 0,
        zoomOut: 95,
      },
      sm: { rounded: "lg" },
    },
    header: {
      display: "flex",
      flexDir: "row",
      justifyContent: "space-between",
      textAlign: "center",
      alignItems: "flex-start",
      sm: { textAlign: "left" },
    },
    footer: {
      display: "flex",
      flexDir: "column-reverse",
      sm: { flexDir: "row", justifyContent: "flex-end" },
    },
    title: {
      fontSize: "lg",
      lineHeight: "none",
      fontWeight: "semibold",
      letterSpacing: "tight",
    },
    description: {
      fontSize: "sm",
      lineHeight: "sm",
    },
  },
});

const { overlay, close, content, header, footer, title, description } =
  dialog.raw();

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(overlay, cssProp);

  return <DialogPrimitive.Overlay ref={ref} className={className} {...props} />;
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    css?: object;
  }
>(({ children, css: cssProp = {}, ...props }, ref) => {
  const className = css(content, cssProp);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content ref={ref} className={className} {...props}>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  children,
  css: cssProp = {},
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { css?: object }) => {
  const className = css(header, cssProp);

  return (
    <div className={className} {...props}>
      {children}
      <DialogPrimitive.Close className={css(close)}>
        <Cross width="1.5rem" stroke="#98A2B3" />
        <span
          className={css({
            srOnly: true,
          })}
        >
          Close
        </span>
      </DialogPrimitive.Close>
    </div>
  );
};
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  css: cssProp = {},
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { css?: object }) => {
  const className = css(footer, cssProp);

  return <div className={className} {...props} />;
};
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(title, cssProp);

  return <DialogPrimitive.Title ref={ref} className={className} {...props} />;
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(description, cssProp);

  return (
    <DialogPrimitive.Description ref={ref} className={className} {...props} />
  );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
